/**
 * RevendasPage - Página de Revendas Premium
 * Migrado para padrão Premium Fleet
 */

import { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StoreIcon from '@mui/icons-material/Store';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import PeopleIcon from '@mui/icons-material/People';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { useManager } from '../common/util/permissions';
import { useEffectAsync } from '../reactHelper';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTableSelection } from '../common/hooks';
import useCrudExports from '../common/hooks/useCrudExports';

// Componentes extraídos
import RevendasTableRow from './components/RevendasTableRow';
import {
  PremiumStatCards,
  PremiumEmptyState,
  BulkActionsBar,
  BulkDeleteDialog,
} from '../common/components/premium';

const RevendasPage = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const manager = useManager();
  const themeMode = tokens?.isDark ? 'dark' : 'light';

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Export menu state
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  // Bulk action states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const tableContainerRef = useRef(null);

  // Fetch de dados
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow('/api/revendas');
      const data = await response.json();
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  // Estatísticas
  const stats = useMemo(() => ({
    total: items.length,
    active: items.filter((i) => !i.disabled).length,
    pending: items.filter((i) => i.status === 'pending').length,
    inactive: items.filter((i) => i.disabled).length,
    totalClients: items.reduce((sum, i) => sum + (i.clientsCount || 0), 0),
  }), [items]);

  // Cards estatísticos para PremiumStatCards
  const statsCards = [
    {
      key: 'total',
      label: t('sharedAll'),
      value: stats.total,
      color: tokens?.accent?.primary,
      Icon: StoreIcon,
      clickable: true,
      onClick: () => setStatusFilter(''),
      active: statusFilter === '',
    },
    {
      key: 'active',
      label: t('sharedActive'),
      value: stats.active,
      color: tokens?.semantic?.success,
      Icon: CheckCircleIcon,
      clickable: true,
      onClick: () => setStatusFilter('active'),
      active: statusFilter === 'active',
    },
    {
      key: 'pending',
      label: t('revendaPending') || 'Pendentes',
      value: stats.pending,
      color: tokens?.semantic?.warning,
      Icon: PendingIcon,
      clickable: true,
      onClick: () => setStatusFilter('pending'),
      active: statusFilter === 'pending',
    },
    {
      key: 'clients',
      label: t('revendaTotalClients') || 'Total Clientes',
      value: stats.totalClients,
      color: tokens?.semantic?.info,
      Icon: PeopleIcon,
      clickable: false,
    },
  ];

  // Filtragem + Busca
  const filteredItems = useMemo(() => {
    let result = items;
    if (statusFilter === 'active') {
      result = result.filter((i) => !i.disabled);
    } else if (statusFilter === 'pending') {
      result = result.filter((i) => i.status === 'pending');
    } else if (statusFilter === 'inactive') {
      result = result.filter((i) => i.disabled);
    }
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (i) =>
          i.title?.toLowerCase().includes(lower) ||
          i.code?.toLowerCase().includes(lower) ||
          i.city?.toLowerCase().includes(lower) ||
          i.state?.toLowerCase().includes(lower) ||
          i.id?.toString().includes(lower)
      );
    }
    return result;
  }, [items, statusFilter, searchText]);

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // Bulk selection hook
  const {
    selectedIds,
    selectedCount,
    toggleSelect,
    selectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    isIndeterminate,
    hasSelection,
  } = useTableSelection(filteredItems);

  // Bulk delete handler
  const handleBulkDelete = useCallback(async () => {
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedIds.map((id) =>
          fetchOrThrow(`/api/revendas/${id}`, { method: 'DELETE' })
        )
      );
      clearSelection();
      setDeleteDialogOpen(false);
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Erro ao excluir revendas:', error);
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedIds, clearSelection]);

  // Handlers
  const handleClearFilters = useCallback(() => {
    setSearchText('');
    setStatusFilter('');
    setPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    setTimestamp(Date.now());
  }, []);

  // Exportações
  const exportColumns = [
    { field: 'id', headerNameKey: 'sharedId' },
    { field: 'code', headerNameKey: 'sharedCodeInternal' },
    { field: 'title', headerNameKey: 'sharedName' },
    { field: 'city', headerNameKey: 'sharedCity' },
    { field: 'state', headerNameKey: 'sharedState' },
    { field: 'clientsCount', headerNameKey: 'revendaClients' },
    {
      field: 'disabled',
      headerNameKey: 'sharedStatus',
      valueGetter: ({ row }) => (row.disabled ? t('sharedDisabled') : t('deviceStatusActive')),
    },
  ];

  const { handleExportCsv, handleExportXlsx, handleExportPdf } = useCrudExports({
    titleKey: 'reportRevendasTitle',
    headerKey: 'reportRevendasHeader',
    rows: filteredItems,
    columns: exportColumns,
    t,
    options: { includeWatermark: true },
  });

  // Check if has active filters
  const hasFilters = Boolean(searchText.trim() || statusFilter);

  // Header styles for premium table
  const headerCellSx = {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: tokens?.text?.secondary,
    borderBottom: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
    py: 1.5,
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['userAdmin', 'sharedRevendas']}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Loading State */}
          {loading && items.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <Typography variant="body1" sx={{ color: tokens?.text?.secondary }}>
                {t('sharedLoading') || 'Carregando...'}
              </Typography>
            </Box>
          ) : (
            <>
              {/* CARDS DE ESTATÍSTICAS */}
              <PremiumStatCards cards={statsCards} />

              {/* BARRA DE AÇÕES */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 1, sm: 1.5 },
                  alignItems: { xs: 'stretch', md: 'center' },
                  flexShrink: 0,
                }}
              >
                <TextField
                  size="small"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                  placeholder={t('sharedSearch')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: tokens?.accent?.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: { xs: '100%', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: tokens?.surface?.glass,
                      borderRadius: tokens?.radius?.md,
                      '&:hover': { backgroundColor: tokens?.surface?.hover },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddCircleIcon />}
                  onClick={() => navigate('/settings/revenda')}
                  sx={{ backgroundColor: tokens?.accent?.primary, '&:hover': { backgroundColor: tokens?.accent?.hover } }}
                >
                  {t('sharedAdd')}
                </Button>
                {manager && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<KeyboardArrowDownIcon />}
                      onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                      sx={{
                        color: tokens?.text?.primary,
                        borderColor: tokens?.border?.default,
                        '&:hover': { borderColor: tokens?.accent?.primary, bgcolor: `${tokens?.accent?.primary}10` },
                      }}
                    >
                      {t('reportExport') || 'Exportar'}
                    </Button>
                    <Menu
                      anchorEl={exportMenuAnchor}
                      open={Boolean(exportMenuAnchor)}
                      onClose={() => setExportMenuAnchor(null)}
                      PaperProps={{
                        sx: {
                          backgroundColor: tokens?.surface?.elevated,
                          border: `1px solid ${tokens?.border?.subtle}`,
                          borderRadius: tokens?.radius?.md,
                        },
                      }}
                    >
                      <MenuItem onClick={() => { handleExportCsv(); setExportMenuAnchor(null); }}>
                        <ListItemIcon><FileDownloadIcon fontSize="small" sx={{ color: tokens?.semantic?.success }} /></ListItemIcon>
                        <ListItemText>CSV</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => { handleExportXlsx(); setExportMenuAnchor(null); }}>
                        <ListItemIcon><TableChartIcon fontSize="small" sx={{ color: tokens?.semantic?.info }} /></ListItemIcon>
                        <ListItemText>Excel</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => { handleExportPdf(); setExportMenuAnchor(null); }}>
                        <ListItemIcon><PictureAsPdfIcon fontSize="small" sx={{ color: tokens?.semantic?.error }} /></ListItemIcon>
                        <ListItemText>PDF</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>

              {/* Results count & Bulk Actions Bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredItems.length} {t('sharedRevendas') || 'revendas'}
                  {hasFilters && ` (${t('sharedFiltered') || 'filtrado'})`}
                </Typography>

                {hasSelection && (
                  <BulkActionsBar
                    selectedCount={selectedCount}
                    onClearSelection={clearSelection}
                    onDelete={() => setDeleteDialogOpen(true)}
                    loading={bulkActionLoading}
                    entityName={t('sharedRevendas') || 'revenda(s)'}
                  />
                )}
              </Box>

              {/* TABELA */}
              <Paper
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  borderRadius: tokens?.radius?.lg || '12px',
                  backgroundColor: tokens?.surface?.glass,
                  border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                }}
              >
                <TableContainer ref={tableContainerRef} className="premium-table-container" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ ...headerCellSx, width: 48 }}>
                          <Checkbox
                            indeterminate={isIndeterminate}
                            checked={isAllSelected}
                            onChange={selectAll}
                            sx={{
                              color: tokens?.text?.tertiary,
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: tokens?.accent?.primary },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...headerCellSx, width: 48 }} />
                        <TableCell sx={{ ...headerCellSx, width: 60 }}>ID</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedCodeInternal') || 'Código'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedName')}</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedCity') || 'Cidade'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedState') || 'Estado'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('revendaClients') || 'Clientes'}</TableCell>
                        <TableCell sx={headerCellSx}>Status</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>{t('sharedActions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} sx={{ border: 0, p: 0 }}>
                            <PremiumEmptyState
                              icon={StoreIcon}
                              title={t('revendasEmpty') || 'Nenhuma revenda cadastrada'}
                              titleFiltered={t('revendasNotFound') || 'Nenhuma revenda encontrada'}
                              subtitle={t('revendasEmptyHint') || 'Comece cadastrando sua primeira revenda'}
                              subtitleFiltered={t('revendasNotFoundHint') || 'Tente ajustar os filtros de busca'}
                              hasFilters={hasFilters}
                              onClearFilters={handleClearFilters}
                              addPath="/settings/revenda"
                              addLabel={t('revendaAdd') || 'Adicionar Revenda'}
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedItems.map((row) => (
                          <RevendasTableRow
                            key={row.id}
                            row={row}
                            themeMode={themeMode}
                            onRefresh={handleRefresh}
                            selected={isSelected(row.id)}
                            onSelect={toggleSelect}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {filteredItems.length > 0 && (
                  <TablePagination
                    component="div"
                    count={filteredItems.length}
                    page={page}
                    onPageChange={(_, newPage) => { setPage(newPage); if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0; }}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage={t('rowsPerPage') || 'Linhas por página'}
                    sx={{
                      borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontSize: '0.8rem',
                        color: tokens?.text?.secondary,
                      },
                    }}
                  />
                )}
              </Paper>
            </>
          )}
        </Box>
      </Box>

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        selectedCount={selectedCount}
        loading={bulkActionLoading}
        entityName={t('sharedRevendas') || 'revenda(s)'}
      />
    </PageLayout>
  );
};

export default RevendasPage;
